import React, { useCallback, useState } from "react"
import { createPortal } from "react-dom"
import PropTypes from "prop-types"
import useTruncationTitle from "../../hooks/useTruncationTitle"

/**
 * Truncated text with a custom tooltip when content overflows.
 * Tip is portaled + fixed so table overflow does not clip the full text.
 * Never sets the native HTML `title` attribute (browser default tooltip).
 */
const TruncatedText = ({
  as: Component = "span",
  children,
  className,
  title: titleProp,
  ...rest
}) => {
  const tooltipSource =
    titleProp != null
      ? titleProp
      : typeof children === "string" || typeof children === "number"
        ? String(children)
        : undefined

  const { ref, title: tipText } = useTruncationTitle(tooltipSource)
  const [tipPos, setTipPos] = useState(null)

  const showTip = useCallback(
    event => {
      if (!tipText) return
      const rect = event.currentTarget.getBoundingClientRect()
      setTipPos({
        x: rect.left + rect.width / 2,
        y: rect.top - 8,
      })
    },
    [tipText],
  )

  const hideTip = useCallback(() => setTipPos(null), [])

  const wrapperClassName = ["amok-truncated", className].filter(Boolean).join(" ")

  return (
    <Component
      {...rest}
      className={wrapperClassName}
      // Strip native browser tooltip — `title` prop is tooltip content only
      title={undefined}
      data-truncated={tipText ? "true" : undefined}
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      onFocus={showTip}
      onBlur={hideTip}
    >
      <span ref={ref} className="amok-truncated__text">
        {children}
      </span>
      {tipText && tipPos && typeof document !== "undefined"
        ? createPortal(
            <span
              className="amok-truncated__tip"
              role="tooltip"
              style={{
                left: `${tipPos.x}px`,
                top: `${tipPos.y}px`,
              }}
            >
              {tipText}
            </span>,
            document.body,
          )
        : null}
    </Component>
  )
}

TruncatedText.propTypes = {
  as: PropTypes.elementType,
  children: PropTypes.node,
  className: PropTypes.string,
  /** Full text for the custom tip when truncated (not a native HTML title). */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

TruncatedText.defaultProps = {
  as: "span",
  children: null,
  className: undefined,
  title: undefined,
}

export default TruncatedText
